const HERMES_API_ORIGIN = import.meta.env.REACT_APP_HERMES_API_BASE;
const HERMES_SUBDOMAIN = import.meta.env.REACT_APP_HERMES_SUBDOMAIN;
const isPreviewEnabled = import.meta.env.REACT_APP_PREVIEW_ENABLED;
const apiStudioActivityLogs = import.meta.env
  .REACT_APP_STUDIO_ACTIVITY_LOGS_API_URL;
const isProdEnv = import.meta.env.PROD;
const trustedDomains = new Set(
  (import.meta.env.REACT_APP_TRUSTED_DOMAINS || "").split(",")
);
const isLocalProxyEnabled = !isProdEnv;

const getPreviewTenant = () => {
  const hostName = window.location.hostname;
  const previewTenant = hostName && hostName.split(".")[0];
  return previewTenant || "walmart";
};

function getTenant() {
  const url = new URL(window.location.href);
  if (isPreviewEnabled) {
    return getPreviewTenant();
  }
  return url.hostname.split(".")[0];
}
export function getHostName() {
  const url = new URL(window.location.href);
  let hostname = url.hostname;

  // Remove "local." prefix if present
  // e.g., internal.local.slackcart.com -> internal.slackcart.com
  if (hostname.includes(".local.")) {
    hostname = hostname.replace(".local.", ".");
  }

  // Remove first subdomain
  // e.g., internal.slackcart.com -> slackcart.com
  return hostname.split(".").slice(1).join(".");
}

export function getAPIOrigin() {
  // In dev mode, use local proxy (window.location.origin)
  if (isLocalProxyEnabled) {
    return window.location.origin;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://previewapi.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) return `https://api.${hostname}`;
  else return "https://"; // any API calls will simply fail and show the service-unavailable page
}

export function getMimirAPIOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/mimir`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://mimir.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) {
    return `https://mimir.${hostname}`;
  } else {
    return "https://"; // any API calls will simply fail and show the service-unavailable page
  }
}

export function getActivityLogsOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/activitylogs`;
  }

  if (isPreviewEnabled) {
    return "https://activitylogs.slackcart.com";
  }
  const hostname = getHostName();
  return `https://activitylogs.${hostname}`;
}

export function getDesignAIAPIOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/heycerta`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://heycerta.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) return `https://heycerta.${hostname}`;
  else return "https://"; // any API calls will simply fail and show the service-unavailable page
}

export function getDesignAIv3APIOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/design-ai`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://design-ai.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) return `https://design-ai.${hostname}`;
  else return "https://"; // any API calls will simply fail and show the service-unavailable page
}

export const getIsRiskAgentAPIEnabled = () => {
  return true;
};

export function getRiskAIAPIOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/risk-ai`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://risk-ai.vetgraph.com";
  }
  const hostname = getHostName();
  const isRiskAgentAPIEnabled = getIsRiskAgentAPIEnabled();

  if (isRiskAgentAPIEnabled) return `https://risk-ai.${hostname}`;
  if (trustedDomains.has(hostname))
    return `https://risk-ai.vetgraph.com`; // TODO: use hostname when API is deployed on all environments
  else return "https://"; // any API calls will simply fail and show the service-unavailable page
}

export function getHermesAPIOrigin() {
  return HERMES_API_ORIGIN;
}

export const getHermesTemporalAPIOrigin = () => {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/hermes-api`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://hermes.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) {
    const subdomain = HERMES_SUBDOMAIN || "hermes";
    return `https://${subdomain}.${hostname}`;
  } else {
    return "https://"; // any API calls will simply fail and show the service-unavailable page
  }
};

export function getCommsAPIOrigin() {
  // Use path-based URL for local proxy
  if (isLocalProxyEnabled) {
    return `${window.location.origin}/comms`;
  }
  //  preview is only available on slackcart
  if (isPreviewEnabled) {
    return "https://comms.slackcart.com";
  }
  const hostname = getHostName();
  if (trustedDomains.has(hostname)) {
    return `https://comms.${hostname}`;
  } else {
    return "https://"; // any API calls will simply fail and show the service-unavailable page
  }
}

// Building via scripts keeps the environment as production which can be confusing
// hence the choice of this variable name to be more verbose

const tenant = getTenant();
const apiBaseURL = `${getAPIOrigin()}/api/v1/`;
const apiBaseURLExt = `${apiBaseURL}ext/`;
const apiBaseURLV2 = `${getAPIOrigin()}/api/v2/`;
const apiBaseURLV3 = `${getAPIOrigin()}/api/v3/`;
const apiBaseURLKronos = `${getAPIOrigin()}/kronos/api/v1/`;
const apiBaseStudio = isLocalProxyEnabled
  ? `${getAPIOrigin()}/studio-api/v1/`
  : `${getAPIOrigin()}/studio/v1/`;
const apiBaseStudioV2 = isLocalProxyEnabled
  ? `${getAPIOrigin()}/studio-api/v2/`
  : `${getAPIOrigin()}/studio/v2/`;
const apiBaseBadgesV1 = `${getAPIOrigin()}/badges/api/v1/`;

const apiBaseURLMimir = `${getMimirAPIOrigin()}/api/v1/`;

// Note:
// Template V2 uses templates/v1 api endpoints
// Template V1 uses certa workflows to serve templates.
const apiBaseTemplatesV2 = `${getAPIOrigin()}/templates/v1/`;

const apiBaseKryptonForComms = `${getAPIOrigin()}/`;

const apiBaseURLActivityLog = `${getActivityLogsOrigin()}/v1/`;
const apiBaseURLAuditLogs = `${getActivityLogsOrigin()}/v1/audit-logs/`;

const apiBaseURLKryptonAuditLogs = `${getAPIOrigin()}/audit-logs/`;

const apiBaseScreening = `${getAPIOrigin()}/screening/`;

const apiBaseSFTP = `${getAPIOrigin()}/sftp/api/`;

const apiBaseKrypton = `${getAPIOrigin()}/`;

const siteOrigin = `${document.location.origin}`;
const certaGoogleLoginURL = siteOrigin + "/api/v1/google_authenticate/form";
const auditLogBaseURL = `${getActivityLogsOrigin()}/v1/`;

/**
 * @deprecated
 */
const supportedFieldFormats = {
  duns: "##-###-####"
};

const envTag = import.meta.env.REACT_APP_SENTRY_TAG;

const getPreviewBasename = () => {
  const pathname = window.location.pathname;
  return pathname && pathname.split("/")[1];
};

const basename = isPreviewEnabled ? `/${getPreviewBasename()}` : "";

export {
  tenant,
  isPreviewEnabled,
  apiBaseURL,
  apiBaseURLExt,
  apiBaseURLMimir,
  apiBaseURLV2,
  apiBaseURLV3,
  apiBaseURLKronos,
  apiBaseURLActivityLog,
  apiBaseStudio,
  apiStudioActivityLogs,
  apiBaseStudioV2,
  apiBaseTemplatesV2,
  apiBaseKryptonForComms,
  apiBaseBadgesV1,
  apiBaseScreening,
  apiBaseURLAuditLogs,
  apiBaseURLKryptonAuditLogs,
  apiBaseSFTP,
  apiBaseKrypton,
  siteOrigin,
  certaGoogleLoginURL,
  auditLogBaseURL,
  supportedFieldFormats,
  envTag,
  basename
};
